import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Copy, ExternalLink, Lock, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useManagerNavigation } from './useManagerNavigation'
import { CrmPanel } from './CrmPanel'
import { ConnectCalendarDialog } from './calendars/ConnectCalendarDialog'
import { useProfile } from '@/api/profile'
import { useCalendars } from '@/api/calendars'
import { api } from '@/lib/api'
import {
  getIntegration,
  INTEGRATIONS,
  type Integration,
} from '@/data/integrations'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

/**
 * Marketplace d'intégrations (persona "manager non-tech").
 *
 * - Sans ?app= → grille des intégrations
 * - Avec ?app=zapier → page détail de l'outil (header + tagline + comment ça
 *   marche + CTA)
 *
 * Le catalogue est statique (cf. src/data/integrations.ts). Les libellés
 * passent par i18n via la convention manager.marketplace.integrations.{id}.*.
 */
export function IntegrationsMarketplaceView() {
  const [searchParams] = useSearchParams()
  const appParam = searchParams.get('app')
  const selectedIntegration = getIntegration(appParam)

  if (selectedIntegration) {
    return <IntegrationDetail integration={selectedIntegration} />
  }
  return <IntegrationsList />
}

// ─────────────────────────────────────────────────────────────────────────────
// Liste
// ─────────────────────────────────────────────────────────────────────────────

/** Square, Wix et Clio sont au forfait Team depuis le 26 oct. 2026, dans des
 * groupes qui restent ouverts : leur carte porte la pastille. Une entreprise déjà
 * reliée garde sa connexion et peut la refaire (le serveur applique la même règle). */
const TEAM_DANS_GROUPE = ['square', 'wix', 'clio']

/** Les intégrations de TEAM_DANS_GROUPE fermées à cette entreprise. */
function useIntegrationsVerrouillees(): (id: string) => boolean {
  const { allowed } = usePlanRights()
  const { data: profile } = useProfile()
  const companyId = profile?.company ?? undefined
  const permis = allowed('crm_equipe')
  const { data: calendars } = useCalendars(permis ? undefined : companyId)
  // Même clé que le panneau Clio : une seule requête.
  const { data: clio } = useQuery({
    queryKey: ['clio', companyId],
    queryFn: async () => (await api.get<{ connected: boolean }>(`/api/companies/${companyId}/clio/`)).data,
    enabled: !permis && Boolean(companyId),
  })
  // Tant que l'état n'est pas chargé, rien n'est verrouillé (pas de clignotement).
  return (id) => {
    if (permis || !TEAM_DANS_GROUPE.includes(id)) return false
    if (id === 'clio') return clio != null && !clio.connected
    return calendars != null && !calendars.some((c) => c.provider === id)
  }
}

/** Les intégrations groupées par usage. `feature` = verrou de plan sur tout le
 * groupe : une seule ligne « Inclus dans Team » en tête, pas une pastille par carte. */
const SECTIONS: ReadonlyArray<{ key: string; ids: string[]; feature?: 'crm_equipe' }> = [
  { key: 'rendezvous', ids: ['google-calendar', 'microsoft-365', 'square', 'wix'] },
  { key: 'crm', ids: ['hubspot', 'pipedrive', 'zoho', 'slack'], feature: 'crm_equipe' },
  { key: 'contacts', ids: ['google-contacts', 'clio'] },
  { key: 'automatisation', ids: ['zapier', 'widget'] },
  { key: 'assistants', ids: ['claude', 'chatgpt'] },
  { key: 'editeurs', ids: ['claude-code', 'cursor', 'vscode', 'codex', 'windsurf'] },
]

function IntegrationsList() {
  const { t } = useTranslation()
  const { allowed, planFor } = usePlanRights()
  const verrouillee = useIntegrationsVerrouillees()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveTab } = useManagerNavigation()

  const goToIntegration = (id: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('app', id)
    setSearchParams(next)
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-8 space-y-7 max-w-5xl">
          {/* Header — titre large + subtitle aéré */}
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">
              {t('manager.marketplace.title')}
            </h1>
            <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
              {t('manager.marketplace.subtitle')}
            </p>
          </div>

          {/* Par usage : un titre, une ligne « Inclus dans Team » si le plan ne
              couvre pas le groupe, puis les cartes en 2 colonnes. */}
          {/* Ce que le plan couvre d'abord ; les groupes réservés à un plan
              supérieur en dernier, la personne n'en a sans doute pas besoin. */}
          {[...SECTIONS]
            .sort((a, b) => Number(!!a.feature && !allowed(a.feature)) - Number(!!b.feature && !allowed(b.feature)))
            .map((section) => {
            const items = section.ids
              .map((id) => INTEGRATIONS.find((i) => i.id === id))
              .filter((i): i is Integration => !!i)
            if (items.length === 0) return null
            const verrouille = section.feature ? !allowed(section.feature) : false
            return (
              <section key={section.key} className="space-y-2.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t(`manager.marketplace.sections.${section.key}`)}
                  </h2>
                  {verrouille && section.feature && <PlanBadge plan={planFor(section.feature)} />}
                </div>
                <div className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-3', verrouille && 'opacity-80')}>
                  {items.map((integration) => (
                    <IntegrationCard
                      key={integration.id}
                      integration={integration}
                      verrouillee={!verrouille && verrouillee(integration.id)}
                      onClick={() => goToIntegration(integration.id)}
                    />
                  ))}
                </div>
              </section>
            )
          })}

          {/* Footer link → Développeurs */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 -mx-2 px-2 pt-6">
            <button
              type="button"
              onClick={() => setActiveTab('integrations')}
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            >
              {t('manager.marketplace.footerLink')} →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────────────────────

interface CardProps {
  integration: Integration
  /** Réservée à un forfait supérieur : une pastille (pas un lien, la carte est déjà un bouton). */
  verrouillee?: boolean
  onClick: () => void
}

function IntegrationCard({ integration, verrouillee = false, onClick }: CardProps) {
  const { t } = useTranslation()
  const isLive = integration.status === 'live'
  const name = t(`manager.marketplace.integrations.${integration.id}.name`)
  const category = t(`manager.marketplace.integrations.${integration.id}.category`)
  const description = t(`manager.marketplace.integrations.${integration.id}.description`)

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isLive}
      className={cn(
        'group flex h-full flex-col text-left rounded-xl border bg-white dark:bg-slate-900 p-4 transition-all',
        'border-slate-200 dark:border-slate-800',
        isLive
          ? 'hover:border-[#E85D3A]/40 hover:shadow-md cursor-pointer'
          : 'opacity-60 cursor-not-allowed',
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 p-2 ring-1 ring-slate-900/5 dark:ring-white/10',
            integration.accentBg,
          )}
        >
          <img src={integration.logo} alt="" className="w-full h-full object-contain" loading="lazy" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white truncate">{name}</h3>
            {!isLive && (
              <span className="px-1.5 py-0.5 text-[10px] leading-none font-bold rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('manager.marketplace.badges.comingSoon')}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">{category}</p>
        </div>
      </div>
      <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug line-clamp-2">{description}</p>
      {verrouillee && (
        <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 text-[12px] font-medium text-blue-600 dark:text-blue-300">
          <Lock className="h-3 w-3" />
          {t('plan.includedIn', { plan: 'Team' })}
        </span>
      )}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Détail
// ─────────────────────────────────────────────────────────────────────────────

interface DetailProps {
  integration: Integration
}

function IntegrationDetail({ integration }: DetailProps) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveTab } = useManagerNavigation()
  const { data: profile } = useProfile()
  const [calendrierOuvert, setCalendrierOuvert] = useState(false)
  const verrouillee = useIntegrationsVerrouillees()

  const goBack = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('app')
    setSearchParams(next)
  }

  const name = t(`manager.marketplace.integrations.${integration.id}.name`)
  const category = t(`manager.marketplace.integrations.${integration.id}.category`)
  const tagline = t(`manager.marketplace.integrations.${integration.id}.tagline`)
  const cta = t(`manager.marketplace.integrations.${integration.id}.cta`)
  const isLive = integration.status === 'live'

  // Les URLs externes peuvent contenir un placeholder {locale} à remplacer
  // au runtime selon la langue active (utile pour /fr/developers vs /en/developers).
  const externalUrl = integration.externalUrl?.replace('{locale}', locale)
  const secondaryUrl = integration.secondaryUrl?.replace('{locale}', locale)
  const secondaryCta = t(`manager.marketplace.integrations.${integration.id}.secondaryCta`, '')

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 pt-12 pb-10 max-w-3xl space-y-8">
          {/* Breadcrumb — marge top plus généreuse pour respirer */}
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('manager.marketplace.backToList')}
          </button>

          {/* Header outil */}
          <div className="flex items-start gap-5">
            <div
              className={cn(
                'w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 p-4 shadow-sm ring-1 ring-slate-900/5 dark:ring-white/10',
                integration.accentBg,
              )}
            >
              <img
                src={integration.logo}
                alt={`${name} logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <p className="text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1.5 font-medium">
                {category}
              </p>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {name}
                </h1>
                {!isLive && (
                  <span className="px-2 py-0.5 text-[10px] leading-none font-bold rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {t('manager.marketplace.badges.comingSoon')}
                  </span>
                )}
              </div>
              <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                {tagline}
              </p>
            </div>
          </div>

          {/* Comment ça marche — visible uniquement si l'intégration est live */}
          {isLive && integration.howItWorksCount > 0 && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-5">
                {t('manager.marketplace.howItWorks')}
              </h2>
              <ul className="space-y-4">
                {Array.from({ length: integration.howItWorksCount }).map((_, idx) => (
                  <li key={idx} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#E85D3A]/10 text-[#E85D3A] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ring-1 ring-[#E85D3A]/20">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {t(`manager.marketplace.integrations.${integration.id}.howItWorks.${idx}.title`)}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                        {t(`manager.marketplace.integrations.${integration.id}.howItWorks.${idx}.description`)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* HubSpot : connexion, réglages, déconnexion */}
          {(integration.id === 'hubspot' || integration.id === 'pipedrive' || integration.id === 'zoho' || integration.id === 'google-contacts' || integration.id === 'slack' || integration.id === 'clio') && <CrmPanel crm={integration.id} />}

          {/* Se branche ailleurs (Calendriers) : un bouton qui y mène */}
          {isLive && integration.ongletDashboard && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                {t(`manager.marketplace.integrations.${integration.id}.brancher`)}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {integration.calendrier && verrouillee(integration.id) && <PlanBadge plan="team" />}
                {integration.calendrier && !verrouillee(integration.id) && (
                  <button
                    type="button"
                    onClick={() => setCalendrierOuvert(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t(`manager.marketplace.integrations.${integration.id}.connecterCta`)}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveTab(integration.ongletDashboard!)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {t(`manager.marketplace.integrations.${integration.id}.brancherCta`)}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              {integration.calendrier && (
                <ConnectCalendarDialog
                  open={calendrierOuvert}
                  onClose={() => setCalendrierOuvert(false)}
                  companyId={profile?.company ?? undefined}
                  initialProvider={integration.calendrier}
                />
              )}
            </div>
          )}

          {/* Assistants IA : le bloc à coller, avec ou sans clé */}
          {isLive && integration.branche && (
            <BlocABrancher integration={integration} />
          )}

          {/* CTAs — primaire orange Callizy + secondaire optionnel ghosted */}
          {isLive && externalUrl ? (
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E85D3A] text-white text-sm font-bold hover:bg-[#d44e2f] transition-colors shadow-lg shadow-[#E85D3A]/20"
              >
                {cta}
                <ExternalLink className="w-4 h-4" />
              </a>
              {secondaryUrl && secondaryCta && (
                <a
                  href={secondaryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {secondaryCta}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          ) : isLive ? null : (
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  {t('manager.marketplace.badges.comingSoon')}
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                {t('manager.marketplace.comingSoonNote')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────────────────
// Bloc à brancher (assistants IA)
// ─────────────────────────────────────────────────────────────────────────────

function BlocABrancher({ integration }: { integration: Integration }) {
  const { t } = useTranslation()
  const [avecCle, setAvecCle] = useState(false)
  const [copie, setCopie] = useState(false)
  const branche = integration.branche!
  // Claude et ChatGPT prennent l'adresse seule : pas de variante avec clé.
  const deuxVariantes = branche.sansCle !== branche.avecCle
  const texte = avecCle ? branche.avecCle : branche.sansCle
  const ou = branche.fichier || t(`manager.marketplace.integrations.${integration.id}.where`, '')

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texte)
      setCopie(true)
      setTimeout(() => setCopie(false), 1800)
    } catch {
      /* presse-papiers refusé */
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        <span className="mr-auto min-w-0 truncate text-[11px] font-mono text-slate-500 dark:text-slate-400">{ou}</span>
        {deuxVariantes && (
          <span className="inline-flex rounded-md bg-slate-200/70 dark:bg-slate-700/60 p-0.5 text-[11px]">
            <button
              type="button"
              onClick={() => setAvecCle(false)}
              className={cn('rounded px-2 py-0.5 transition-colors', !avecCle ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400')}
            >
              {t('manager.marketplace.mcp.signIn')}
            </button>
            <button
              type="button"
              onClick={() => setAvecCle(true)}
              className={cn('rounded px-2 py-0.5 transition-colors', avecCle ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400')}
            >
              {t('manager.marketplace.mcp.apiKey')}
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={copier}
          aria-label={t('manager.marketplace.mcp.copy')}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700 hover:bg-white dark:hover:bg-slate-800 transition-colors"
        >
          {copie ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copie ? t('manager.marketplace.mcp.copied') : t('manager.marketplace.mcp.copy')}
        </button>
      </div>
      <pre className="p-4 font-mono text-[13px] leading-6 overflow-x-auto whitespace-pre text-slate-800 dark:text-slate-200 bg-white dark:bg-[#0a1220]">
        {texte}
      </pre>
      <p className="px-4 pb-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        {avecCle && deuxVariantes
          ? t('manager.marketplace.mcp.noteKey')
          : t(`manager.marketplace.integrations.${integration.id}.note`, t('manager.marketplace.mcp.noteSignIn'))}
      </p>
    </div>
  )
}
